import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
} from "@mui/material";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { toast } from "react-toastify";
import { Checklist } from "../CustomComponents/ChecklistInterface";
import MultiSubheaderTableRowComponent from "../CustomComponents/SubheaderTableRowComponent";
import googleDriveAxios from "../../common/googleDriveAxios";

const ElectricalStationChecklist: React.FC<{
  chassisNo: string;
  setModel: React.Dispatch<React.SetStateAction<string>>;
  model: string;
  setCheckedBy: React.Dispatch<React.SetStateAction<string>>;
  isDisabled: boolean;
}> = ({ chassisNo, setModel, model, setCheckedBy, isDisabled }) => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState<boolean>(false);
  const [
    externalElectricalTestInspection,
    setExternalElectricalTestInspection,
  ] = useState<Checklist[]>([]);
  const [
    internalElectricalTestInspection,
    setInternalElectricalTestInspection,
  ] = useState<Checklist[]>([]);
  const [appliance, setAppliance] = useState<Checklist[]>([]);
  const [electricalOverheadCupboard, setElectricalOverheadCupboard] = useState<
    Checklist[]
  >([]);
  const [conduits, setConduits] = useState<Checklist[]>([]);
  const [other, setOther] = useState<Checklist[]>([]);
  const [checkedBy, setLocalCheckedBy] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [apiError, setApiError] = useState<boolean>(false);
  const [isRadioChanged, setIsRadioChanged] = useState<boolean>(false);
  const [completeStatus, setCompleteStatus] = useState<string>("Incomplete");
  const hasUnsavedChanges = chassisNo;

  const [activeCheckpointList, setActiveCheckpointList] = useState<any[]>([]);
  const checkCheckpointRecord = React.useCallback(async () => {
    if (chassisNo) {
      try {
        const queryParams = new URLSearchParams({
          chassis_no: "DC" + chassisNo,
          dept_name: "electrical-inspection",
        }).toString();

        const response = await googleDriveAxios.post(
          `/checkpoint-image-record?${queryParams}`
        );
        const data = response.data;
        setActiveCheckpointList(data.recorded_checkpoint_list);
        console.log(data.recorded_checkpoint_list);
      } catch (error) {
        console.error("Error checking images:", error);
      }
    }
  }, [chassisNo]);

  useEffect(() => {
    checkCheckpointRecord();
  }, [checkCheckpointRecord]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const fetchData = async () => {
      if (!chassisNo || chassisNo.length < 4 || chassisNo.length > 5) {
        setExternalElectricalTestInspection([]);
        setInternalElectricalTestInspection([]);
        setAppliance([]);
        setElectricalOverheadCupboard([]);
        setConduits([]);
        setOther([]);
        setApiError(false);
        setModel("");
        setCompleteStatus("Incomplete");
        setIsRadioChanged(false);
        return;
      }

      setLoading(true);
      setApiError(false);
      setModel("");
      try {
        const response = await axiosPrivate.post(
          `/prepare_electrical_inspection/${chassisNo}`
        );
        const data = response.data;

        const externalData = (data.externalElectricalTestInspection || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const internalData = (data.internalElectricalTestInspection || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const applianceData = (data.appliance || []).map((item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        }));
        const cupboardData = (data.electricalOverheadCupboard || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const conduitsData = (data.conduits || []).map((item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        }));
        const otherData = (data.other || []).map((item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        }));

        setExternalElectricalTestInspection(externalData);
        setInternalElectricalTestInspection(internalData);
        setAppliance(applianceData);
        setElectricalOverheadCupboard(cupboardData);
        setConduits(conduitsData);
        setOther(otherData);
        setLocalCheckedBy(data.checkedBy || "");
        setCheckedBy(data.checkedBy || "");
        setModel(data.model || "");

        let formattedDate = "";
        if (data.createdOn) {
          const date = new Date(data.createdOn);
          if (date.getFullYear() !== 1900) {
            formattedDate = data.createdOn ? data.createdOn.split("T")[0] : "";
          }
        }
        setDate(formattedDate);

        const allItems = [
          ...externalData,
          ...internalData,
          ...applianceData,
          ...cupboardData,
          ...conduitsData,
          ...otherData,
        ];
        const isComplete =
          allItems.length > 0 &&
          allItems.every(
            (item) =>
              item.status === "pass" ||
              item.status === "fail" ||
              item.status === "n/a"
          );
        setCompleteStatus(isComplete ? "Complete" : "Incomplete");
        setIsRadioChanged(false);
      } catch (error: any) {
        if (error.response?.status === 500) {
          setApiError(true);
        } else {
          toast.error("Failed to fetch data.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [chassisNo, setModel, axiosPrivate, setCheckedBy]);

  const saveData = async () => {
    if (!date.trim()) {
      toast.error("Date is required.");
      return;
    }
    if (!checkedBy.trim()) {
      toast.error("Checked By field is required.");
      return;
    }

    setLoading(true);
    try {
      const allChecklists = [
        ...externalElectricalTestInspection,
        ...internalElectricalTestInspection,
        ...appliance,
        ...electricalOverheadCupboard,
        ...conduits,
        ...other,
      ];

      if (isRadioChanged) {
        const isComplete =
          allChecklists.length > 0 &&
          allChecklists.every(
            (item) =>
              item.status === "pass" ||
              item.status === "fail" ||
              item.status === "n/a"
          );
        setCompleteStatus(isComplete ? "Complete" : "Incomplete");
      }

      const payload = {
        chassisNo,
        model: model,
        externalElectricalTestInspection: externalElectricalTestInspection.map(
          (item) => ({
            title: item.title,
            notes: item.notes,
            status: item.status,
          })
        ),
        internalElectricalTestInspection: internalElectricalTestInspection.map(
          (item) => ({
            title: item.title,
            notes: item.notes,
            status: item.status,
          })
        ),
        appliance: appliance.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        electricalOverheadCupboard: electricalOverheadCupboard.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        conduits: conduits.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        other: other.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        checkedBy,
        createdOn: date,
        completeStatus,
      };

      await toast.promise(
        axiosPrivate.post("/create_or_update_electrical_inspection", payload),
        {
          pending: "Saving data...",
          success: "Data saved successfully!",
          error: "Failed to save data.",
        }
      );

      setIsRadioChanged(false);

      if (checkedBy) {
        const allChecklists = [
          ...externalElectricalTestInspection,
          ...internalElectricalTestInspection,
          ...appliance,
          ...electricalOverheadCupboard,
          ...conduits,
          ...other,
        ];

        let itemNoCounter = 1;
        const newReworkItems = allChecklists
          .filter((item) => item.status === "fail")
          .flatMap((item) => {
            const title = item.title || "No Title Available";
            const notesStr = item.notes || "";

            if (notesStr.includes(";")) {
              const parts = notesStr
                .split(";")
                .map((part) => part.trim())
                .filter((part) => part);
              return parts.map((note) => ({
                itemNo: itemNoCounter++,
                problemDescription: `${title}: ${note}`,
                comment: "",
                whoFoundFault: checkedBy,
                responsible: "",
                whoDidRework: "",
                timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                completed: false,
              }));
            } else {
              return [
                {
                  itemNo: itemNoCounter++,
                  problemDescription: `${title}: ${notesStr}`,
                  comment: "",
                  whoFoundFault: checkedBy,
                  responsible: "",
                  whoDidRework: "",
                  timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                  completed: false,
                },
              ];
            }
          });

        if (newReworkItems.length > 0) {
          const reworkPayload = {
            chassisNo,
            model,
            station_name: "electrical-inspection",
            reworkItems: newReworkItems,
          };

          await axiosPrivate.post(`rework/save`, reworkPayload, {
            params: { rework_type: "electrical-inspection" },
          });
        }
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save data.");
    } finally {
      setLoading(false);
    }
  };

  const handleNotesChange = (index: number, value: string, type: string) => {
    if (completeStatus === "Complete") return;

    if (type === "external_electrical_test_inspection") {
      const updatedData = [...externalElectricalTestInspection];
      updatedData[index].notes = value;
      setExternalElectricalTestInspection(updatedData);
    }
    if (type === "internal_electrical_test_inspection") {
      const updatedData = [...internalElectricalTestInspection];
      updatedData[index].notes = value;
      setInternalElectricalTestInspection(updatedData);
    }
    if (type === "appliance") {
      const updatedData = [...appliance];
      updatedData[index].notes = value;
      setAppliance(updatedData);
    }
    if (type === "electrical_overhead_cupboard") {
      const updatedData = [...electricalOverheadCupboard];
      updatedData[index].notes = value;
      setElectricalOverheadCupboard(updatedData);
    }
    if (type === "conduit") {
      const updatedData = [...conduits];
      updatedData[index].notes = value;
      setConduits(updatedData);
    }
    if (type === "other") {
      const updatedData = [...other];
      updatedData[index].notes = value;
      setOther(updatedData);
    }
  };

  const handleRadioChange = (index: number, value: string, type: string) => {
    if (completeStatus === "Complete") return;

    setIsRadioChanged(true);

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].status = value.toLowerCase();
      setChecklist(updatedData);
    };

    if (type === "external_electrical_test_inspection") {
      updateChecklist(
        externalElectricalTestInspection,
        setExternalElectricalTestInspection
      );
    }
    if (type === "internal_electrical_test_inspection") {
      updateChecklist(
        internalElectricalTestInspection,
        setInternalElectricalTestInspection
      );
    }
    if (type === "appliance") {
      updateChecklist(appliance, setAppliance);
    }
    if (type === "electrical_overhead_cupboard") {
      updateChecklist(
        electricalOverheadCupboard,
        setElectricalOverheadCupboard
      );
    }
    if (type === "conduit") {
      updateChecklist(conduits, setConduits);
    }
    if (type === "other") {
      updateChecklist(other, setOther);
    }
  };

  const allChecklists = [
    ...externalElectricalTestInspection,
    ...internalElectricalTestInspection,
    ...appliance,
    ...electricalOverheadCupboard,
    ...conduits,
    ...other,
  ];

  const allNotChecked =
    allChecklists.length > 0 &&
    allChecklists.every((item) => item.status?.toLowerCase() === "not checked");

  console.log("All Checklists:", allChecklists);
  console.log("allNotChecked:", allNotChecked);

  const checklistsWithSerialNumbers = [
    {
      data: externalElectricalTestInspection,
      type: "external_electrical_test_inspection",
      subHeaderText: "External - Electrical Test Inspection",
    },
    {
      data: internalElectricalTestInspection,
      type: "internal_electrical_test_inspection",
      subHeaderText: "Internal - Electrical Test Inspection",
    },
    { data: appliance, type: "appliance", subHeaderText: "Appliance" },
    {
      data: electricalOverheadCupboard,
      type: "electrical_overhead_cupboard",
      subHeaderText: "Electrical Overhead Cupboard",
    },
    { data: conduits, type: "conduit", subHeaderText: "Conduits" },
    { data: other, type: "other", subHeaderText: "Other" },
  ];

  let serialNumberCounter = 0;

  return (
    <Box
      sx={{
        position: "relative",
        filter: loading ? "blur(5px)" : "none",
        pointerEvents: loading ? "none" : "auto",
      }}
    >
      <Typography
        sx={{
          mb: 2,
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          color:
            completeStatus === "Incomplete" ? "error.main" : "success.main",
        }}
      >
        {completeStatus === "Incomplete" ? (
          <>⚠️ INCOMPLETE</>
        ) : (
          <>
            <OfflinePinIcon /> COMPLETE
          </>
        )}
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow className="table__head_row">
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              ></TableCell>
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              >
                S/N
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                CHECKLIST
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                NOTES
              </TableCell>
              <TableCell
                className="table__head_cell"
                sx={{ width: "5%" }}
                align="center"
              >
                STATUS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center"></TableCell>
              </TableRow>
            ) : apiError ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="error">Invalid DC Number</Typography>
                </TableCell>
              </TableRow>
            ) : (
              <React.Fragment>
                {checklistsWithSerialNumbers.map((checklistSection) => {
                  const serialNumberStart = serialNumberCounter + 1;
                  serialNumberCounter += checklistSection.data.length;

                  console.log(
                    `Rendering ${checklistSection.type} with serialNumberStart: ${serialNumberStart}, item count: ${checklistSection.data.length}`
                  );

                  return (
                    <MultiSubheaderTableRowComponent
                      key={checklistSection.type}
                      subHeaderText={checklistSection.subHeaderText}
                      type={checklistSection.type}
                      data={checklistSection.data}
                      handleNotesChange={handleNotesChange}
                      handleRadioChange={handleRadioChange}
                      disabled={completeStatus === "Complete"}
                      allNotChecked={allNotChecked}
                      serialNumberStart={serialNumberStart}
                      chassisDCNumber={"DC" + chassisNo}
                      deptName={"electrical-inspection"}
                      activeCheckpointList={activeCheckpointList}
                      checkCheckpointRecord={checkCheckpointRecord}
                    />
                  );
                })}
              </React.Fragment>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container spacing={3} my={4}>
        <Grid item md={4}>
          <TextField
            label="Checked By"
            value={checkedBy}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setLocalCheckedBy(e.target.value);
              setCheckedBy(e.target.value);
            }}
            fullWidth
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
        <Grid item md={4}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setDate(e.target.value);
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
      </Grid>
      <Button
        variant="contained"
        color="primary"
        onClick={saveData}
        disabled={
          allChecklists.length === 0 ||
          apiError ||
          loading ||
          (!chassisNo && !model) ||
          completeStatus === "Complete"
        }
      >
        Save
      </Button>
    </Box>
  );
};

export default ElectricalStationChecklist;
